// .pen: F2SMnk (ds/selfie/generation-panel), Ldys1 (generation-orb),
// wUqPR/n7uMK/Zfjq4 (generation-step--done/current/pending)
import { useEffect, useState } from 'react';
import { Check, CircleNotch, Clock, Sparkle } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

import { Button } from '../../ui/Button';
import pt from '../../../locales/pt';
import { ReportMessage } from './ReportPrimitives';

const STEP_KEYS = ['balances', 'evolution', 'categories', 'summary'];
const STEP_INTERVAL_MS = 2500;

function GenOrb() {
  return (
    <div className="relative flex h-[120px] w-[120px] items-center justify-center">
      <span className="absolute inset-0 rounded-full bg-sage-glow blur-xl" aria-hidden />
      <svg viewBox="0 0 104 104" className="absolute h-[104px] w-[104px]" aria-hidden>
        <circle cx="52" cy="52" r="46" fill="none" strokeWidth="10" className="stroke-border" />
        <motion.circle
          cx="52"
          cy="52"
          r="46"
          fill="none"
          strokeWidth="10"
          strokeLinecap="round"
          className="stroke-sage-500"
          strokeDasharray={2 * Math.PI * 46}
          initial={{ strokeDashoffset: 2 * Math.PI * 46 * 0.7 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'linear' }}
          style={{ transformOrigin: '52px 52px', strokeDashoffset: 2 * Math.PI * 46 * 0.7 }}
        />
      </svg>
      <motion.span
        className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-sage-500 to-sage-600 text-sage-on-solid shadow-lg shadow-sage-600/40"
        animate={{ scale: [1, 1.06, 1], opacity: [0.9, 1, 0.9] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Sparkle className="h-7 w-7" aria-hidden />
      </motion.span>
    </div>
  );
}

function StepRow({ stateKey, label }) {
  if (stateKey === 'done') {
    return (
      <li className="flex items-center gap-space-3 rounded-ds-md px-space-3 py-space-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sage-solid text-sage-on-solid">
          <Check className="h-3 w-3" weight="bold" aria-hidden />
        </span>
        <span className="flex-1 text-type-body-sm font-medium text-muted-foreground">{label}</span>
        <span className="text-type-chip text-muted-foreground">feito</span>
      </li>
    );
  }
  if (stateKey === 'now') {
    return (
      <li className="flex items-center gap-space-3 rounded-ds-md border border-sage-border bg-sage-tint px-space-3 py-space-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-sage-500 bg-card text-sage-600">
          <CircleNotch className="h-3.5 w-3.5 animate-spin" aria-hidden />
        </span>
        <span className="flex-1 text-type-body-sm font-semibold text-sage-on-tint">{label}</span>
        <span className="text-type-chip font-semibold text-sage-on-tint">agora</span>
      </li>
    );
  }
  return (
    <li className="flex items-center gap-space-3 rounded-ds-md px-space-3 py-space-2">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-input-border bg-muted">
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" aria-hidden />
      </span>
      <span className="flex-1 text-type-body-sm text-muted-foreground">{label}</span>
      <span className="text-type-chip text-muted-foreground">a seguir</span>
    </li>
  );
}

/**
 * Ecrã de geração do relatório IA — orbe + passos com antecipação.
 */
export function SelfieReportGenerating({
  previousPeriodLabel,
  slowGeneration = false,
  onRefetch,
  isFetching = false,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const copy = pt.snapshots.report.generation;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveStep((prev) => (prev < STEP_KEYS.length - 1 ? prev + 1 : prev));
    }, STEP_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div
      className="mx-auto flex w-full max-w-lg flex-col items-center gap-space-5 rounded-ds-xl border border-border bg-card px-space-5 py-space-8 shadow-sm"
      role="status"
      aria-live="polite"
    >
      <GenOrb />

      <div className="flex flex-col items-center gap-space-2 text-center">
        <p className="text-type-section font-bold uppercase tracking-[0.1em] text-sage-on-tint">
          A preparar a tua análise
        </p>
        <h1 className="text-type-title-page font-semibold tracking-tight text-foreground">
          {copy.title}
        </h1>
        <p className="text-type-body-sm leading-relaxed text-muted-foreground">
          {previousPeriodLabel ? copy.compareWith.replace('{period}', previousPeriodLabel) : pt.snapshots.report.generatingHint}
        </p>
      </div>

      <ul className="flex w-full flex-col gap-space-1 pt-space-1">
        {STEP_KEYS.map((key, index) => {
          const stateKey = index < activeStep ? 'done' : index === activeStep ? 'now' : 'wait';
          return <StepRow key={key} stateKey={stateKey} label={copy.steps[key]} />;
        })}
      </ul>

      {slowGeneration ? (
        <div className="flex w-full flex-col items-center gap-space-3 border-t border-border pt-space-4">
          <ReportMessage
            tone="warning"
            icon={Clock}
            title={pt.snapshots.report.slowGeneration}
            className="w-full"
          >
            A análise continua a ser gerada em segundo plano. Podes verificar agora ou voltar mais
            tarde, não perdes nada.
          </ReportMessage>
          {onRefetch ? (
            <Button variant="secondary" size="sm" onClick={onRefetch} disabled={isFetching}>
              {pt.snapshots.report.checkNow}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default SelfieReportGenerating;
